// Citizen satisfaction survey shown on /pqrsd/estado after a FINAL answer.
//
// Admins edit it in Config → PQRSD → Encuesta (stored as config/pqrsd.citizenSurvey).
// The citizen picks one rating option, which records the rating and closes the
// PQRSD, or the mandatory reopen option, which needs a reason plus a written
// detail and reopens it with the full legal term.
//
// One file is the source of truth for both sides: handlePqrsdPublic validates
// answers with it, and the admin screen imports the same defaults and checks.
// Plain CommonJS so the functions can require it and webpack can import it.

const MAX_OPTIONS = 10;
const MAX_REASONS = 15;
const MAX_LABEL = 80;
const MAX_BUTTON_LABEL = 40;
const MAX_QUESTION = 200;
const MIN_DETAIL = 10;
const MAX_DETAIL = 2000;
// The fixed last reason: always offered, never editable, never removable.
const OTHER_REASON_ID = "other";
const OTHER_REASON_LABEL = "Otro";

const DEFAULT_CITIZEN_SURVEY = {
  question: "¿Qué tan satisfecho está con la respuesta a su solicitud?",
  options: [
    { id: "very_satisfied", label: "Muy satisfecho", score: 5 },
    { id: "satisfied", label: "Satisfecho", score: 4 },
    { id: "neutral", label: "Ni satisfecho ni insatisfecho", score: 3 },
    { id: "unsatisfied", label: "Poco satisfecho", score: 2 },
  ],
  reopen: {
    label: "No estoy satisfecho — reabrir la solicitud",
    // The button that sends the reopening (the option above only selects it).
    buttonLabel: "Reabrir la solicitud",
    reasons: [
      { id: "not_answered", label: "No respondieron lo que pregunté" },
      { id: "missing_info", label: "La respuesta está incompleta" },
      { id: "unclear", label: "No entiendo la respuesta" },
      { id: "wrong", label: "La información es incorrecta" },
    ],
  },
};

const clean = (v, max) => String(v == null ? "" : v).replace(/\s+/g, " ").trim().slice(0, max);

// Stable ids for new rows: the stored rating keeps the id, so renaming an option
// later does not orphan old answers.
function slugId(label, taken) {
  const base = clean(label, MAX_LABEL).toLowerCase().normalize("NFD")
    .replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "option";
  let id = base;
  for (let n = 2; taken.has(id) || id === OTHER_REASON_ID; n += 1) id = `${base}_${n}`;
  taken.add(id);
  return id;
}

function normalizeRows(rows, { withScore, max }) {
  const taken = new Set();
  const out = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const label = clean(row?.label, MAX_LABEL);
    if (!label || out.length >= max) return;
    let id = clean(row?.id, 60);
    if (!id || taken.has(id) || id === OTHER_REASON_ID) id = slugId(label, taken);
    else taken.add(id);
    const item = { id, label };
    if (withScore) {
      const score = Math.round(Number(row?.score));
      item.score = Number.isFinite(score) ? Math.min(5, Math.max(1, score)) : 3;
    }
    out.push(item);
  });
  return out;
}

// Whatever is stored (possibly partial, legacy or hand-edited) as a usable
// survey. Missing pieces fall back to the defaults; an empty option list does
// too, because a survey without rating options could only reopen.
function normalizeCitizenSurvey(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const options = normalizeRows(src.options, { withScore: true, max: MAX_OPTIONS });
  const reasons = normalizeRows(src.reopen?.reasons, { withScore: false, max: MAX_REASONS });
  return {
    question: clean(src.question, MAX_QUESTION) || DEFAULT_CITIZEN_SURVEY.question,
    options: options.length ? options : DEFAULT_CITIZEN_SURVEY.options.map((o) => Object.assign({}, o)),
    reopen: {
      label: clean(src.reopen?.label, MAX_LABEL) || DEFAULT_CITIZEN_SURVEY.reopen.label,
      buttonLabel: clean(src.reopen?.buttonLabel, MAX_BUTTON_LABEL) || DEFAULT_CITIZEN_SURVEY.reopen.buttonLabel,
      // An empty list is allowed: "Otro" is always there.
      reasons: Array.isArray(src.reopen?.reasons) ? reasons : DEFAULT_CITIZEN_SURVEY.reopen.reasons.map((r) => Object.assign({}, r)),
    },
  };
}

// The admin form's problems, in Spanish, or [] when it can be saved.
function validateCitizenSurvey(draft) {
  const errors = [];
  const options = Array.isArray(draft?.options) ? draft.options : [];
  const reasons = Array.isArray(draft?.reopen?.reasons) ? draft.reopen.reasons : [];
  if (!clean(draft?.question, MAX_QUESTION)) errors.push("Escriba la pregunta de la encuesta.");
  if (!options.length) errors.push("Agregue al menos una opción de calificación.");
  if (options.length > MAX_OPTIONS) errors.push(`Máximo ${MAX_OPTIONS} opciones de calificación.`);
  if (options.some((o) => !clean(o?.label, MAX_LABEL))) errors.push("Todas las opciones necesitan un texto.");
  if (options.some((o) => !(Number(o?.score) >= 1 && Number(o?.score) <= 5))) {
    errors.push("Cada opción necesita un puntaje entre 1 y 5.");
  }
  const dupes = (rows) => {
    const seen = new Set();
    return rows.some((r) => {
      const k = clean(r?.label, MAX_LABEL).toLowerCase();
      if (!k) return false;
      if (seen.has(k)) return true;
      seen.add(k);
      return false;
    });
  };
  if (dupes(options)) errors.push("Hay opciones de calificación repetidas.");
  if (!clean(draft?.reopen?.label, MAX_LABEL)) errors.push("Escriba el texto de la opción para reabrir.");
  if (!clean(draft?.reopen?.buttonLabel, MAX_BUTTON_LABEL)) errors.push("Escriba el texto del botón para reabrir.");
  if (reasons.length > MAX_REASONS) errors.push(`Máximo ${MAX_REASONS} motivos de reapertura.`);
  if (reasons.some((r) => !clean(r?.label, MAX_LABEL))) errors.push("Todos los motivos necesitan un texto.");
  if (dupes([...reasons, { label: OTHER_REASON_LABEL }])) errors.push("Hay motivos repetidos (\"Otro\" ya existe siempre).");
  return errors;
}

// What the public page renders: the survey plus the fixed "Otro" reason.
function publicSurvey(raw) {
  const s = normalizeCitizenSurvey(raw);
  return {
    question: s.question,
    options: s.options.map(({ id, label }) => ({ id, label })),
    reopen: {
      label: s.reopen.label,
      buttonLabel: s.reopen.buttonLabel,
      reasons: [...s.reopen.reasons, { id: OTHER_REASON_ID, label: OTHER_REASON_LABEL }],
      minDetail: MIN_DETAIL,
      maxDetail: MAX_DETAIL,
    },
  };
}

// A citizen's rating answer → { option } (a snapshot of label and score, so a
// later edit of the survey does not rewrite history) or { error }.
function resolveRating(raw, optionId) {
  const s = normalizeCitizenSurvey(raw);
  const option = s.options.find((o) => o.id === String(optionId || ""));
  if (!option) return { error: "La opción elegida ya no está disponible. Recargue la página e intente de nuevo." };
  return { option: Object.assign({}, option) };
}

// A citizen's reopen answer → { reason: { id, label }, detail } or { error }.
// `legacyReason` is the free text the previous page and old email links send:
// it is accepted as "Otro" with that text as the detail.
function resolveReopen(raw, { reasonId, detail, legacyReason } = {}) {
  const s = normalizeCitizenSurvey(raw);
  const text = String(detail ?? legacyReason ?? "").trim().slice(0, MAX_DETAIL);
  const id = String(reasonId || (legacyReason != null ? OTHER_REASON_ID : ""));
  const reason = id === OTHER_REASON_ID
    ? { id: OTHER_REASON_ID, label: OTHER_REASON_LABEL }
    : s.reopen.reasons.find((r) => r.id === id);
  if (!reason) return { error: "Elija el motivo por el que reabre la solicitud." };
  if (text.length < MIN_DETAIL) {
    return { error: `Explique con detalle qué necesita (mínimo ${MIN_DETAIL} caracteres).` };
  }
  return { reason: Object.assign({}, reason), detail: text };
}

module.exports = {
  DEFAULT_CITIZEN_SURVEY,
  OTHER_REASON_ID,
  OTHER_REASON_LABEL,
  MIN_DETAIL,
  MAX_DETAIL,
  MAX_OPTIONS,
  MAX_REASONS,
  MAX_LABEL,
  MAX_BUTTON_LABEL,
  normalizeCitizenSurvey,
  validateCitizenSurvey,
  publicSurvey,
  resolveRating,
  resolveReopen,
};
